import {WidgetType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/reportsWorkspaceCapability/domain/ReportJson";
import AreaChart from "~/svgs/areaChart.svg?react";
import MultiBarChart from "~/svgs/charts/multiBarGraph.svg?react";
import LineChart from "~/svgs/lineChart.svg?react";
import ComboChart from "~/svgs/charts/comboChart.svg?react";
import DonutChart from "~/svgs/charts/donutChart.svg?react";
import HorizontalBarChart from "~/svgs/charts/horizontalBarChart.svg?react";
import StackedBarChart from "~/svgs/charts/stackedBarGraph.svg?react";
import Gauge from "~/svgs/gauge.svg?react";
import ImageWidget from "~/svgs/imageWidget.svg?react";
import NumberTile from "~/svgs/numberTile.svg?react";
import PieChart from "~/svgs/pieChart.svg?react";
import ProgressBar from "~/svgs/progressBar.svg?react";
import Table from "~/svgs/table.svg?react";
import TextWidget from "~/svgs/textWidget.svg?react";
import BarChartIncomplete from "~/svgs/barChartIncomplete.svg?react";
import LineChartIncomplete from "~/svgs/lineChartIncomplete.svg?react";
import PieChartIncomplete from "~/svgs/pieChartIncomplete.svg?react";
import TableWidgetIncomplete from "~/svgs/tableIncomplete.svg?react";
import GaugeIncomplete from "~/svgs/gaugeIncomplete.svg?react";
import ProgressBarIncomplete from "~/svgs/progressBarIncomplete.svg?react";
import NumberTileIncomplete from "~/svgs/numberTileIncomplete.svg?react";
import type {EnumDictionary} from "~/typeDefinitions";
import type {StoredWidget} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/reportsWorkspaceCapability/domain/ReportJson";

export const widgetTypeDetails: EnumDictionary<
    StoredWidget["type"],
    {
        humanReadableName: string;
        icon: React.ComponentType<{
            className?: string;
        }>;
        incompleteIcon: React.ComponentType<{
            className?: string;
        }>;
        isComingSoon?: boolean;
        // TODO: Rename isAffectedByTimeRange to hasStaticData
        /** Defines whether the widget data is static, ie, never-changing.
         * Think something like a text widget, whose text never changes.
         */
        isAffectedByTimeRange: boolean;
        hasLimitedAccess: boolean;
        minWidth: number;
        minHeight: number;
        doesRelyOnHades: boolean;
        doesConsumeDimensions: boolean;
        canHaveSecondaryAxis: boolean;
    }
> = {
    [WidgetType.multiBarGraph]: {
        humanReadableName: "Bar Graph",
        icon: MultiBarChart,
        incompleteIcon: BarChartIncomplete,
        isAffectedByTimeRange: true,
        hasLimitedAccess: false,
        minWidth: 4,
        minHeight: 4,
        doesRelyOnHades: true,
        doesConsumeDimensions: true,
        canHaveSecondaryAxis: true,
    },
    [WidgetType.stackedBarGraph]: {
        humanReadableName: "Stacked Bar Graph",
        icon: StackedBarChart,
        incompleteIcon: BarChartIncomplete,
        isAffectedByTimeRange: true,
        hasLimitedAccess: false,
        minWidth: 4,
        minHeight: 4,
        doesRelyOnHades: true,
        doesConsumeDimensions: true,
        canHaveSecondaryAxis: false,
    },
    [WidgetType.horizontalBarGraph]: {
        humanReadableName: "Horizontal Bar Graph",
        icon: HorizontalBarChart,
        incompleteIcon: BarChartIncomplete,
        isAffectedByTimeRange: true,
        hasLimitedAccess: false,
        minWidth: 4,
        minHeight: 4,
        doesRelyOnHades: true,
        doesConsumeDimensions: true,
        canHaveSecondaryAxis: true,
    },
    [WidgetType.lineGraph]: {
        humanReadableName: "Line Graph",
        icon: LineChart,
        incompleteIcon: LineChartIncomplete,
        isAffectedByTimeRange: true,
        hasLimitedAccess: false,
        minWidth: 4,
        minHeight: 4,
        doesRelyOnHades: true,
        doesConsumeDimensions: true,
        canHaveSecondaryAxis: true,
    },
    [WidgetType.comboChart]: {
        humanReadableName: "Combo Chart",
        icon: ComboChart,
        incompleteIcon: BarChartIncomplete,
        isAffectedByTimeRange: true,
        hasLimitedAccess: false,
        minWidth: 4,
        minHeight: 4,
        doesRelyOnHades: true,
        doesConsumeDimensions: true,
        canHaveSecondaryAxis: true,
    },
    [WidgetType.numberTile]: {
        humanReadableName: "Stat",
        icon: NumberTile,
        incompleteIcon: NumberTileIncomplete,
        isAffectedByTimeRange: true,
        hasLimitedAccess: false,
        minWidth: 2,
        minHeight: 2,
        doesRelyOnHades: true,
        doesConsumeDimensions: false,
        canHaveSecondaryAxis: false,
    },
    [WidgetType.pieChart]: {
        humanReadableName: "Pie Chart",
        icon: PieChart,
        incompleteIcon: PieChartIncomplete,
        isAffectedByTimeRange: true,
        hasLimitedAccess: false,
        minWidth: 3,
        minHeight: 3,
        doesRelyOnHades: true,
        doesConsumeDimensions: true,
        canHaveSecondaryAxis: false,
    },
    [WidgetType.donutChart]: {
        humanReadableName: "Donut Chart",
        icon: DonutChart,
        incompleteIcon: PieChartIncomplete,
        isAffectedByTimeRange: true,
        hasLimitedAccess: false,
        minWidth: 3,
        minHeight: 3,
        doesRelyOnHades: true,
        doesConsumeDimensions: true,
        canHaveSecondaryAxis: false,
    },
    [WidgetType.text]: {
        humanReadableName: "Text",
        icon: TextWidget,
        incompleteIcon: NumberTileIncomplete,
        isAffectedByTimeRange: false,
        hasLimitedAccess: false,
        minWidth: 1,
        minHeight: 1,
        doesRelyOnHades: false,
        doesConsumeDimensions: false,
        canHaveSecondaryAxis: false,
    },
    [WidgetType.table]: {
        humanReadableName: "Table",
        icon: Table,
        incompleteIcon: TableWidgetIncomplete,
        isAffectedByTimeRange: true,
        hasLimitedAccess: false,
        minWidth: 4,
        minHeight: 4,
        doesRelyOnHades: true,
        doesConsumeDimensions: true,
        canHaveSecondaryAxis: false,
    },
    [WidgetType.gauge]: {
        humanReadableName: "Gauge",
        icon: Gauge,
        incompleteIcon: GaugeIncomplete,
        isAffectedByTimeRange: true,
        hasLimitedAccess: false,
        isComingSoon: true,
        minWidth: 3,
        minHeight: 3,
        doesRelyOnHades: true,
        doesConsumeDimensions: false,
        canHaveSecondaryAxis: false,
    },
    [WidgetType.progressBar]: {
        humanReadableName: "Progress Bar",
        icon: ProgressBar,
        incompleteIcon: ProgressBarIncomplete,
        isAffectedByTimeRange: true,
        hasLimitedAccess: false,
        isComingSoon: true,
        minWidth: 3,
        minHeight: 3,
        doesRelyOnHades: true,
        doesConsumeDimensions: false,
        canHaveSecondaryAxis: false,
    },
    [WidgetType.image]: {
        humanReadableName: "Image",
        icon: ImageWidget,
        incompleteIcon: NumberTileIncomplete,
        isComingSoon: true,
        isAffectedByTimeRange: false,
        hasLimitedAccess: false,
        minWidth: 2,
        minHeight: 2,
        doesRelyOnHades: true,
        doesConsumeDimensions: false,
        canHaveSecondaryAxis: false,
    },
    [WidgetType.areaGraph]: {
        humanReadableName: "Area Graph",
        icon: AreaChart,
        incompleteIcon: LineChartIncomplete,
        isAffectedByTimeRange: true,
        hasLimitedAccess: false,
        minWidth: 4,
        minHeight: 4,
        doesRelyOnHades: true,
        doesConsumeDimensions: true,
        canHaveSecondaryAxis: true,
    },
};
